import { format } from "date-fns";
import CustomSelect from "../../components/ui/CustomSelect";
import React, { useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { AppLayout } from "src/components/layouts";
import AdminService from "src/services/admin.services";
import "react-toastify/dist/ReactToastify.css";
import { useQuery } from "react-query";

type Sender = {
  name: string;
  email: string;
  phone: string;
};

type SupportMessage = {
  _id: string;
  comment: string;
  created_at: string;
  restaurant?: Sender[];
  user?: Sender[];
  is_responded_to?: boolean;
};

const adminService = new AdminService();

const SupportMessagesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(15);
  const [selectedMessage, setSelectedMessage] = useState<SupportMessage | null>(
    null
  );
  const [respondingId, setRespondingId] = useState<string | null>(null);

  const fetchMessages = async () => {
    const response = await adminService.getSupports(page, size);
    return response || [];
  };

  const {
    data: messages = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["supportMessages", page, size],
    queryFn: fetchMessages,
    refetchInterval: 60000, // Refetch every 60 seconds
  });

  const hasNext = messages.length === size;

  const markAsResponded = async (id: string) => {
    setRespondingId(id);
    try {
      const response = await adminService.markSupportAsResponded(id);
      if (response?.message === "support message updated successfully") {
        toast.success("Marked as responded successfully");
        refetch(); // Refresh after mutation
      } else {
        toast.error("Failed to mark as responded.");
      }
    } catch (err) {
      console.error("Mark as responded error:", err);
      toast.error("An error occurred while updating the message.");
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <AppLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="min-h-screen flex flex-col container mx-auto px-4 py-6 bg-amber-50 rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-2">
          <h1 className="text-2xl font-bold text-amber-600">
            Customer Support
          </h1>
          <div className="flex items-center gap-2">
            <label htmlFor="pageSize" className="text-sm text-gray-700">
              Messages per page:
            </label>
            <CustomSelect
              value={size}
              onChange={(value) => {
                setSize(Number(value));
                setPage(1);
              }}
              options={[5, 10, 15, 20, 50, 100].map((n) => ({
                label: n.toString(),
                value: n,
              }))}
              placeholder="Select page size"
              size="middle"
              allowClear={false}
            />
          </div>
        </div>

        <div className="flex-grow">
          {isLoading ? (
            <div className="flex justify-center items-center py-20 text-gray-600">
              <FaSpinner className="animate-spin mr-2" /> Loading...
            </div>
          ) : isError ? (
            <p className="text-red-600 text-center">
              Failed to fetch support messages.
            </p>
          ) : messages.length === 0 ? (
            <p className="text-center text-gray-500">No messages found.</p>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {messages.map((msg) => {
                  const sender = msg.restaurant?.[0] || msg.user?.[0];
                  const senderType = msg.restaurant?.length
                    ? "Restaurant"
                    : "User";

                  return (
                    <div
                      key={msg._id}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col justify-between"
                    >
                      <div className="text-sm mb-2">
                        <p className="text-amber-500 font-medium">
                          {senderType}
                        </p>
                        <p className="font-semibold text-gray-800">
                          {sender?.name}
                        </p>
                        <p className="text-xs text-gray-600">{sender?.email}</p>
                        <p className="text-xs text-gray-600">{sender?.phone}</p>
                      </div>

                      <p className="text-sm text-gray-700 mb-2 line-clamp-3">
                        {msg.comment}
                      </p>

                      <div className="flex justify-between items-center mt-auto">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {format(new Date(msg.created_at), "PP")}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedMessage(msg)}
                            className="text-xs text-amber-600 hover:underline"
                          >
                            View
                          </button>
                          <button
                            disabled={
                              msg.is_responded_to || respondingId === msg._id
                            }
                            onClick={() => markAsResponded(msg._id)}
                            className={`px-3 py-1 text-xs rounded font-medium flex items-center justify-center gap-2 transition ${
                              msg.is_responded_to || respondingId === msg._id
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-amber-500 text-white hover:bg-amber-600"
                            }`}
                          >
                            {msg.is_responded_to ? (
                              "Responded"
                            ) : respondingId === msg._id ? (
                              <>
                                <FaSpinner className="animate-spin w-3 h-3" />
                                <span>Marking...</span>
                              </>
                            ) : (
                              "Mark as Responded"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Modal Viewer */}
              {selectedMessage && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm">
                  <div className="bg-white rounded-lg max-w-md w-full p-6 relative shadow-xl">
                    <button
                      className="absolute top-2 right-3 text-gray-600 hover:text-black text-xl"
                      onClick={() => setSelectedMessage(null)}
                    >
                      &times;
                    </button>
                    <h2 className="text-lg font-bold text-amber-600 mb-3">
                      Full Message
                    </h2>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedMessage.comment}
                    </p>
                    <div className="mt-4 text-xs text-gray-500">
                      From:{" "}
                      {selectedMessage.restaurant?.[0]?.name ||
                        selectedMessage.user?.[0]?.name}{" "}
                      | Sent:{" "}
                      {format(new Date(selectedMessage.created_at), "PPpp")}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        <div className="py-4 px-6 border-t border-amber-200">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`px-4 py-2 rounded text-sm font-medium ${
                page === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-amber-500 text-white hover:bg-amber-600"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext}
              className={`px-4 py-2 rounded text-sm font-medium ${
                hasNext
                  ? "bg-amber-500 text-white hover:bg-amber-600"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SupportMessagesPage;
